import { useCallback } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type {
	BlockMenuExtensionConfiguration,
	ExtensionConfiguration,
	SelectionExtensionPlugin,
	ToolbarExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { expVal } from '@confluence/feature-experiments';

import { CreateSnippetModal } from '../modals/CreateSnippetModal.tsx';
import { messages } from './useCreateSnippetExtension.i18n.ts';

export type UseCreateSnippetExtensionProps = {
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
};

export const useCreateSnippetExtension = ({
	editorAPIRef,
}: UseCreateSnippetExtensionProps): ExtensionConfiguration[] => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();

	const fireCreateSnippetButtonClickedAnalytics = useCallback(
		(selectionLength: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'createSnippet',
					type: 'ui',
					attributes: {
						selectionLength,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleCreateSnippet = useCallback(() => {
		if (!editorAPIRef?.current) {
			return;
		}

		const actions = editorAPIRef?.current?.selectionExtension?.actions;
		const document = actions?.getDocumentFromSelection();
		const docAdf: ADFEntity | undefined = document?.selectedNodeAdf;

		// getDocumentFromSelection will always return adf of type 'doc', so strip this doc wrapper for the saved content;
		const adfContent = docAdf?.content?.filter((content) => content !== undefined);

		if (!adfContent || adfContent.length === 0) {
			return;
		}

		fireCreateSnippetButtonClickedAnalytics(JSON.stringify(adfContent)?.length);
		showModal(CreateSnippetModal, { adf: docAdf, editorAPIRef });
	}, [editorAPIRef, fireCreateSnippetButtonClickedAnalytics, showModal]);

	if (!expVal('cc_snippets', 'isEnabled', false)) {
		return [];
	}

	const toolbarConfiguration: ToolbarExtensionConfiguration = {
		getMenuItems: () => {
			return [
				{
					label: formatMessage(messages.createBlockTemplate),
					lozenge: {
						label: formatMessage(messages.createBlockTemplateLozenge),
					},
					icon: TemplateIcon,
					onClick: () => {
						void handleCreateSnippet();
					},
				},
			];
		},
	};

	/**
	 * placement: 'featured' allows the button to show up on the top level of the
	 * block menu, instead of under the Turn Into -> Create subsection
	 */
	const blockMenuConfiguration: BlockMenuExtensionConfiguration = {
		placement: 'featured',
		getMenuItems: () => {
			return [
				{
					label: formatMessage(messages.createBlockTemplate),
					lozenge: {
						label: formatMessage(messages.createBlockTemplateLozenge),
					},
					icon: TemplateIcon,
					onClick: () => {
						void handleCreateSnippet();
					},
				},
			];
		},
	};

	return [
		{
			key: 'create-snippet',
			source: 'first-party',
			inlineToolbar: toolbarConfiguration,
			blockMenu: blockMenuConfiguration,
		},
	];
};
